import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Loader2, Package, Plus, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useAllMaterials, useSaveMaterial } from '@/hooks/useInventoryMaster'
import { useStorageConditions } from '@/hooks/useStorage'
import {
  keptAtFilterText,
  NO_KEPT_AT_MESSAGE,
  splitByKeptAt,
  UNSTATED_LABEL,
  usableKeptAt,
} from '@/lib/putAwayFilter'
import { KeptAtBadge } from './KeptAtBadge'
import { CREATABLE_MATERIAL_TYPES, type ApiMaterial } from '@/types/inventory'
import type { StorageCondition } from '@/types/storage'

/**
 * Pick the material a receipt is for — search, filter chips, and a scrolling
 * list grouped under section headers. A plain dropdown gave one line per
 * material and no way to narrow by kind, which is unusable once a catalogue
 * runs past a couple of dozen entries.
 *
 * A material has to be chosen first because a lot belongs to one — and if it is
 * not in the catalogue yet, it can be created here rather than abandoning the
 * receipt to go and add it somewhere else.
 *
 * Its own module because two callers need it: the Receive stock button, and
 * putting stock straight into a slot from the storage map.
 */
export function MaterialPickerDialog({
  open,
  onClose,
  onPick,
  description = 'Pick what arrived. You will record how many and where it went next.',
  destinationCondition,
}: {
  open: boolean
  onClose: () => void
  onPick: (material: ApiMaterial) => void
  /** Overridden when the destination is already known, e.g. a chosen slot. */
  description?: string
  /**
   * Set when the destination is already fixed — receiving straight into a
   * slot clicked on the storage map (`PutAwayPanel`). Narrows the list to
   * material this location can actually keep, the same way `AddLotModal`
   * narrows its unit picker once the material is known — just run the other
   * direction, since here the slot comes first. Omitted from the plain
   * "Receive stock" entry point, where no destination has been chosen yet and
   * the unit picker does this narrowing afterward instead.
   */
  destinationCondition?: StorageCondition | null
}) {
  // Loaded when the picker opens, not with the page that holds it (z8nrz7e8kw).
  const materials = useAllMaterials({}, { enabled: open })
  const conditions = useStorageConditions()
  const [query, setQuery] = useState('')
  const [type, setType] = useState('all')
  const [creating, setCreating] = useState(false)
  const [showUnstated, setShowUnstated] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery('')
      setType('all')
      setCreating(false)
      setShowUnstated(false)
    }
  }, [open])

  const allItems = useMemo(() => materials.data?.items ?? [], [materials.data])
  const conditionMeta = useMemo(() => conditions.data?.conditions ?? [], [conditions.data])

  // Only narrowed when a destination is fixed — z8nrz7dyd2 AC6. The plain
  // "Receive stock" flow (no destinationCondition) shows every material, as
  // before; AddLotModal's own unit picker narrows once the material is known.
  //
  // With a destination, materials needing exactly its Kept at are listed;
  // those whose master data states no temperature sit in their own collapsed
  // group; anything needing another temperature is never offered. A
  // destination with no Kept at (null) offers nothing — see `noKeptAt`.
  // A non-persistent destination (a controlled-rate freezer) counts as none.
  const fixed = destinationCondition !== undefined
  const destination = fixed ? usableKeptAt(destinationCondition, conditionMeta) : null
  const noKeptAt = fixed && destination === null
  const split = useMemo(
    () => (destination ? splitByKeptAt(allItems, destination) : null),
    [allItems, destination],
  )
  const items = fixed ? (split?.matching ?? []) : allItems
  const narrowing = destination ? keptAtFilterText(destination, conditionMeta) : null

  const types = useMemo(
    () => [...new Set(items.map((m) => m.materialType).filter(Boolean))].sort(),
    [items],
  )

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (m: ApiMaterial) => {
      if (type !== 'all' && m.materialType !== type) return false
      if (!q) return true
      return (
        m.materialCode.toLowerCase().includes(q) ||
        m.materialName.toLowerCase().includes(q) ||
        (m.materialId ?? '').toLowerCase().includes(q)
      )
    }
  }, [query, type])
  const filtered = useMemo(() => items.filter(matches), [items, matches])
  const unstated = useMemo(() => (split?.unstated ?? []).filter(matches), [split, matches])

  // Grouped under the material kind, so a long catalogue reads as sections
  // rather than one undifferentiated list.
  const sections = useMemo(() => {
    const byType = new Map<string, ApiMaterial[]>()
    for (const m of filtered) {
      const key = m.materialType || 'Other'
      const list = byType.get(key) ?? []
      list.push(m)
      byType.set(key, list)
    }
    return [...byType.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => ({
        name,
        items: list.sort((a, b) => a.materialCode.localeCompare(b.materialCode)),
      }))
  }, [filtered])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl'>
        <DialogHeader className='border-b border-gray-100 px-6 pb-4 pt-5'>
          <DialogTitle>Receive stock</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {creating ? (
          <NewMaterialForm onCancel={() => setCreating(false)} onCreated={onPick} />
        ) : (
          <>
            <div className='space-y-2.5 border-b border-gray-100 px-6 py-3'>
              <div className='relative'>
                <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Search by code or name…'
                  className='h-9 pl-8'
                />
              </div>
              {types.length > 1 && (
                <div className='flex flex-wrap gap-1.5'>
                  {['all', ...types].map((t) => (
                    <button
                      key={t}
                      type='button'
                      onClick={() => setType(t)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        type === t
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-gray-200 bg-white text-muted-foreground hover:bg-gray-50',
                      )}
                    >
                      {t === 'all' ? 'All kinds' : t}
                    </button>
                  ))}
                </div>
              )}
              {/* States what the list is showing and why, the same way
                  AddLotModal's unit picker does when IT narrows — "why isn't
                  my material here" has to be answerable from this screen. */}
              {narrowing && <p className='text-xs text-muted-foreground'>{narrowing}</p>}
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto px-6 py-3'>
              {materials.isLoading && (
                <p className='py-8 text-center text-sm text-muted-foreground'>
                  <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
                  Loading materials…
                </p>
              )}
              {noKeptAt ? (
                <p className='my-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                  {NO_KEPT_AT_MESSAGE}
                </p>
              ) : (
                !materials.isLoading &&
                sections.length === 0 && (
                  <p className='py-8 text-center text-sm text-muted-foreground'>
                    {query
                      ? `Nothing matches “${query}”.`
                      : fixed
                        ? 'No materials need this temperature.'
                        : 'No materials yet.'}
                  </p>
                )
              )}
              {sections.map((section) => (
                <div key={section.name} className='mb-3'>
                  <p className='sticky top-0 z-10 -mx-1 bg-background px-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                    {section.name}
                    <span className='ml-1.5 font-normal normal-case text-gray-300'>
                      ({section.items.length})
                    </span>
                  </p>
                  <ul className='space-y-1'>
                    {section.items.map((m) => (
                      <li key={m.id}>
                        <MaterialRow material={m} onPick={onPick} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {unstated.length > 0 && (
                <div className='mb-3 border-t pt-2'>
                  <button
                    type='button'
                    aria-expanded={showUnstated}
                    onClick={() => setShowUnstated((v) => !v)}
                    className='flex w-full items-center gap-1.5 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-foreground'
                  >
                    <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', showUnstated && 'rotate-90')} />
                    {UNSTATED_LABEL}
                    <span className='font-normal normal-case text-gray-400'>({unstated.length})</span>
                  </button>
                  {showUnstated && (
                    <ul className='space-y-1'>
                      {unstated.map((m) => (
                        <li key={m.id}>
                          <MaterialRow material={m} onPick={onPick} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className='flex items-center justify-between gap-2 border-t border-gray-100 px-6 py-3'>
              {/* Stock can arrive for something not in the catalogue yet.
                  Without this the receipt had to be abandoned to go add it. */}
              {noKeptAt ? (
                <span />
              ) : (
                <Button variant='outline' size='sm' className='gap-1.5' onClick={() => setCreating(true)}>
                  <Plus className='h-3.5 w-3.5' />
                  Not listed — add a material
                </Button>
              )}
              <Button variant='ghost' size='sm' onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function MaterialRow({ material: m, onPick }: { material: ApiMaterial; onPick: (m: ApiMaterial) => void }) {
  return (
    <button
      type='button'
      onClick={() => onPick(m)}
      className='group flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5'
    >
      <Package className='h-4 w-4 shrink-0 text-muted-foreground' />
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-baseline gap-2'>
          <span className='font-mono text-xs font-semibold'>{m.materialCode}</span>
          <span className='truncate text-sm'>{m.materialName}</span>
        </div>
        <div className='mt-0.5 flex flex-wrap items-center gap-2'>
          {/* What it must be kept at, so the wrong shelf is obvious before
              the receipt, not after. */}
          <KeptAtBadge condition={m.requiresCondition ?? null} />
          <span className='text-[11px] text-muted-foreground'>{m.totalQuantity ?? 0} on hand</span>
        </div>
      </div>
      <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary' />
    </button>
  )
}

/** The minimum a material needs to exist: a code, a name, and what kind it is. */
function NewMaterialForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (material: ApiMaterial) => void
}) {
  const saveMaterial = useSaveMaterial()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState('Consumable')
  const valid = code.trim() !== '' && name.trim() !== ''

  return (
    <div className='space-y-3 px-6 py-4'>
      <p className='text-sm font-medium'>New material</p>
      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='space-y-1'>
          <Label>Code</Label>
          <Input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder='CONS-TUBE-003'
            className='font-mono'
          />
        </div>
        <div className='space-y-1'>
          <Label>Kind</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREATABLE_MATERIAL_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className='space-y-1'>
        <Label>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='Cryo Vial 5mL' />
      </div>
      <p className='text-[11px] text-muted-foreground'>
        Creates the catalogue entry so this receipt has something to belong to. Storage conditions
        and profiles can be filled in later under Inventory.
      </p>
      {saveMaterial.error && (
        <p className='text-sm text-destructive'>{String(saveMaterial.error.message)}</p>
      )}
      <div className='flex justify-end gap-2'>
        <Button variant='ghost' size='sm' onClick={onCancel}>
          Back
        </Button>
        <Button
          size='sm'
          disabled={!valid || saveMaterial.isPending}
          onClick={() => {
            const trimmed = code.trim().toUpperCase()
            saveMaterial.mutate(
              {
                payload: {
                  // No materialId — the server mints the MAT-###### key.
                  materialCode: trimmed,
                  materialName: name.trim(),
                  materialType: type,
                },
              },
              { onSuccess: (created) => onCreated(created as ApiMaterial) },
            )
          }}
        >
          {saveMaterial.isPending ? 'Creating…' : 'Create and receive'}
        </Button>
      </div>
    </div>
  )
}
